import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    QueryList,
    SimpleChanges,
    ViewChildren,
    ViewEncapsulation,
} from '@angular/core';
import {
    IChatConversation,
    IChatSuggestion,
    INewConversationData,
    IOpenConversationManageUsersData,
} from '@fi-shared/fi-chat/fi-chat';
import { FiChatHelperService } from '@fi-shared/fi-chat/fi-chat-helper.service';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FiChatService } from '@fi-shared/fi-chat/fi-chat.service';
import { ILightWeightUser } from '@fi-shared/models';
import { cloneDeep } from '@fi-shared/utils';
import { FiChatConversationListItemComponent } from '@fi-shared/fi-chat/fi-chat-conversation-list/fi-chat-conversation-list-item/fi-chat-conversation-list-item.component';
import { FiChatSyncService } from '@fi-shared/fi-chat/fi-chat-sync.service';
import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { getTotalMilliseconds } from '@fi-shared/utils/get-total-milliseconds';

@Component({
    selector: 'fi-chat-conversation-list',
    template: `
        @if (loading$ | async) {
          <div class="conversations-loading-container">
            <fi-loading></fi-loading>
          </div>
        } @else {
          @for (conversation of filteredConversationsList$ | async; track conversation) {
            @if (!conversation.IsHidden) {
              <fi-chat-conversation-list-item
                [conversation]="conversation"
                [isConversationMenuAvailable]="isConversationMenuAvailable"
                (openExistingConversation)="onOpenExistingConversation(conversation)"
                (closeConversation)="onCloseConversation($event)"
                (createNewConversation)="onCreateNewGroupConversation($event)"
                (openManageUsersConversation)="onOpenManageUsersConversation($event)"
                >
              </fi-chat-conversation-list-item>
            }
          }
          @for (suggestedConversation of filteredSuggestionsList$ | async; track suggestedConversation) {
            <fi-chat-conversation-suggestion-item
              [suggestedConversation]="suggestedConversation"
              (openNewChatWindow)="onOpenNewConversation($event)"
            ></fi-chat-conversation-suggestion-item>
          }
        }
        
        `,
    styleUrls: ['./fi-chat-conversation-list.component.scss'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: { class: 'fi-chat-conversation-list' },
    standalone: false,
})
export class FiChatConversationListComponent implements OnInit, OnChanges, OnDestroy {
    @Input() searchValue = '';
    @Input() updateSuggestionsList$: Observable<void> = new Observable<void>();
    @Input() isConversationMenuAvailable: boolean;
    @Output() openExistingConversation = new EventEmitter<IChatConversation>();
    @Output() openNewConversation = new EventEmitter<ILightWeightUser>();
    @Output() closeConversation = new EventEmitter<IChatConversation>();
    @Output() openManageUsersConversation = new EventEmitter<IOpenConversationManageUsersData>();
    @Output() createNewGroupConversation = new EventEmitter<INewConversationData>();
    private chatConversations: IChatConversation[] = [];
    private chatSuggestions: IChatSuggestion[] = [];
    filteredConversationsList$ = new BehaviorSubject<IChatConversation[]>([]);
    filteredSuggestionsList$ = new BehaviorSubject<IChatSuggestion[]>([]);
    loading$ = new BehaviorSubject<boolean>(false);

    @ViewChildren(FiChatConversationListItemComponent)
    chatConversationListItemComponents: QueryList<FiChatConversationListItemComponent>;

    constructor(
        private fiChatHelperService: FiChatHelperService,
        private fiChatService: FiChatService,
        private fiChatSyncService: FiChatSyncService,
        private cdr: ChangeDetectorRef,
    ) {}

    ngOnInit(): void {
        this.fiChatSyncService.conversationsUpdated$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(($event) => {
            // now we should receive all conversations and sort them by latest activity
            const arr: IChatConversation[] = this.fiChatSyncService.conversations.slice(0);
            arr.sort(
                comparator((item) => {
                    if (!item.LatestActivity) return 0;
                    return getTotalMilliseconds(item.LatestActivity);
                }, true),
            );

            this.chatConversations = arr;
            this.updateConversationsList();
        });

        this.fiChatService.suggestedConversations$
            .pipe(takeUntil(this.ngOnDestroy$))
            .subscribe((suggestedConversations: IChatSuggestion[]) => {
                if (!suggestedConversations.length) return;

                this.chatSuggestions = cloneDeep(suggestedConversations);
                if (this.isUnitSuggestionNotFullyLoaded()) this.chatSuggestions.length = 1;

                this.updateSuggestionsList();
            });

        this.updateSuggestionsList$.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
            this.fiChatHelperService.increaseVisibleSuggestionLimit(this.isUnitSuggestionNotFullyLoaded());
            this.fiChatService.triggerSuggestedConversationUpdate();
        });
    }

    onOpenManageUsersConversation($event: IOpenConversationManageUsersData) {
        this.openManageUsersConversation.emit($event);
    }

    onCreateNewGroupConversation($event: INewConversationData) {
        this.createNewGroupConversation.emit($event);
    }

    onCloseConversation($event: IChatConversation) {
        this.closeConversation.emit($event);
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes.searchValue) {
            this.fiChatHelperService.resetVisibleSuggestionCounter();
            this.updateConversationsList();
            this.updateSuggestionsList();
        }
    }

    onOpenExistingConversation(conversation: IChatConversation) {
        this.openExistingConversation.emit(conversation);
    }

    onOpenNewConversation(user: ILightWeightUser) {
        this.openNewConversation.emit(user);
    }

    private isUnitSuggestionNotFullyLoaded(): boolean {
        return (
            this.chatSuggestions.length > 0 &&
            this.chatSuggestions[0].Users.length > this.fiChatHelperService.visibleSuggestionLimit
        );
    }

    private updateConversationsList() {
        this.filteredConversationsList$.next(this.filterChatConversations(this.chatConversations));
        this.cdr.detectChanges();
    }

    private updateSuggestionsList() {
        if (!!this.chatSuggestions.length)
            this.filteredSuggestionsList$.next(this.filterChatSuggestions(this.chatSuggestions));
    }

    private filterChatConversations(conversations: IChatConversation[]): IChatConversation[] {
        return this.fiChatHelperService.filterChatConversations(conversations, this.searchValue);
    }

    private filterChatSuggestions(suggestions: IChatSuggestion[]): IChatSuggestion[] {
        return this.fiChatHelperService.filterChatSuggestions(
            suggestions,
            this.searchValue,
            this.isUnitSuggestionNotFullyLoaded(),
        );
    }

    private ngOnDestroy$ = new Subject<void>();
    ngOnDestroy(): void {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
